import { useEffect } from 'react'

/**
 * The distance in pixels the mouse must move before a press becomes a drag.
 */
const DRAG_THRESHOLD = 5

/**
 * Elements that keep their own mouse behaviour and never start a drag.
 */
const INTERACTIVE_SELECTOR =
  'input, textarea, select, button, a, [contenteditable="true"], .MuiDataGrid-cell--editing'

/**
 * Lets mouse users pan any overflowing data grid horizontally by dragging its body.
 *
 * Touch input is not affected, as it already scrolls natively.
 */
export default function useDataGridDragScroll(): void {
  useEffect(() => {
    function handleMouseDown(event: MouseEvent) {
      if (event.button !== 0 || !(event.target instanceof Element)) return

      if (event.target.closest(INTERACTIVE_SELECTOR) !== null) return

      const element = event.target.closest<HTMLElement>('.MuiDataGrid-virtualScroller')

      if (element === null || element.scrollWidth <= element.clientWidth) return

      const scroller = element
      const startX = event.clientX
      const startScrollLeft = scroller.scrollLeft
      let dragging = false

      function handleMouseMove(event: MouseEvent) {
        const dx = event.clientX - startX

        if (!dragging) {
          if (Math.abs(dx) < DRAG_THRESHOLD) return

          dragging = true
          document.body.style.cursor = 'grabbing'
          document.body.style.userSelect = 'none'
        }

        scroller.scrollLeft = startScrollLeft - dx
      }

      function handleMouseUp() {
        window.removeEventListener('mousemove', handleMouseMove)
        window.removeEventListener('mouseup', handleMouseUp)

        if (!dragging) return

        document.body.style.cursor = ''
        document.body.style.userSelect = ''

        // Swallow the click that follows the drag so row and cell click handlers do not fire.
        window.addEventListener('click', handleClick, { capture: true, once: true })
        // No click follows when the mouse is released outside the window, so stop waiting for it.
        setTimeout(() => window.removeEventListener('click', handleClick, { capture: true }))
      }

      function handleClick(event: MouseEvent) {
        event.stopPropagation()
        event.preventDefault()
      }

      window.addEventListener('mousemove', handleMouseMove)
      window.addEventListener('mouseup', handleMouseUp)
    }

    document.addEventListener('mousedown', handleMouseDown)

    return () => document.removeEventListener('mousedown', handleMouseDown)
  }, [])
}
